import { type ReactNode } from 'react';
import styled from 'styled-components';

import { rubikTypographyStyles } from './typography.styles';

type ProfileSummaryCardVariant = 'default' | 'compact' | 'preview';
type ProfileSummaryCardProgressTrackTone = 'neutral' | 'surface';

type ProfileSummaryCardProps = {
  avatar: ReactNode;
  avatarSlotWidthPx?: number;
  dataUi: string;
  goalText: string;
  profileName: string;
  progressLabel: string | null;
  progressPercent: number | null;
  progressTrackTone?: ProfileSummaryCardProgressTrackTone;
  ratingSlot?: ReactNode;
  variant?: ProfileSummaryCardVariant;
};

type ProfileSummaryCardMetrics = {
  backgroundTone: 'base' | 'subtle';
  borderRadiusPx: number;
  gapPx: number;
  goalLineHeightPx: number;
  goalTextSizePx: number;
  identityColumnGapPx: number;
  identityRowGapPx: number;
  nameLineHeightPx: number;
  nameTextSizePx: number;
  paddingPx: number;
  progressHeightPx: number;
  progressLabelLineHeightPx: number;
  progressLabelSizePx: number;
  progressRadiusPx: number;
};

const profileSummaryCardMetricsByVariant: Record<
  ProfileSummaryCardVariant,
  ProfileSummaryCardMetrics
> = {
  compact: {
    backgroundTone: 'base',
    borderRadiusPx: 8,
    gapPx: 8,
    goalLineHeightPx: 10,
    goalTextSizePx: 8,
    identityColumnGapPx: 3,
    identityRowGapPx: 11,
    nameLineHeightPx: 12,
    nameTextSizePx: 10,
    paddingPx: 10,
    progressHeightPx: 17,
    progressLabelLineHeightPx: 9,
    progressLabelSizePx: 8,
    progressRadiusPx: 58,
  },
  preview: {
    backgroundTone: 'base',
    borderRadiusPx: 10,
    gapPx: 10,
    goalLineHeightPx: 13,
    goalTextSizePx: 11,
    identityColumnGapPx: 5,
    identityRowGapPx: 12,
    nameLineHeightPx: 16,
    nameTextSizePx: 13,
    paddingPx: 13,
    progressHeightPx: 23,
    progressLabelLineHeightPx: 11,
    progressLabelSizePx: 11,
    progressRadiusPx: 76,
  },
  default: {
    backgroundTone: 'subtle',
    borderRadiusPx: 11,
    gapPx: 9,
    goalLineHeightPx: 15,
    goalTextSizePx: 12,
    identityColumnGapPx: 8,
    identityRowGapPx: 11,
    nameLineHeightPx: 17,
    nameTextSizePx: 14,
    paddingPx: 12,
    progressHeightPx: 25,
    progressLabelLineHeightPx: 14,
    progressLabelSizePx: 12,
    progressRadiusPx: 84,
  },
};

function resolveProgressTrackBackground(
  tone: ProfileSummaryCardProgressTrackTone,
  theme: { colors: { surface: { progressTrack: string } } },
) {
  if (tone === 'neutral') {
    return '#d2d2d2';
  }

  return theme.colors.surface.progressTrack;
}

const Card = styled.article.attrs<{ $dataUi: string }>(({ $dataUi }) => ({
  'data-ui': $dataUi,
}))<{ $metrics: ProfileSummaryCardMetrics }>`
  width: 100%;
  box-sizing: border-box;
  border-radius: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.borderRadiusPx)};
  background: ${({ $metrics, theme }) =>
    $metrics.backgroundTone === 'base'
      ? theme.colors.surface.base
      : theme.colors.surface.subtle};
  padding: ${({ $metrics, theme }) => theme.scale.px($metrics.paddingPx)};
  display: grid;
  gap: ${({ $metrics, theme }) => theme.scale.px($metrics.gapPx)};
`;

const IdentityRow = styled.div<{
  $avatarSlotWidthPx: number;
  $metrics: ProfileSummaryCardMetrics;
}>`
  display: grid;
  grid-template-columns:
    ${({ $avatarSlotWidthPx, theme }) => theme.scale.px($avatarSlotWidthPx)}
    minmax(0, 1fr);
  gap: ${({ $metrics, theme }) => theme.scale.px($metrics.identityRowGapPx)};
`;

const AvatarSlot = styled.div`
  min-width: 0;
`;

const IdentityColumn = styled.div<{ $metrics: ProfileSummaryCardMetrics }>`
  min-width: 0;
  display: grid;
  gap: ${({ $metrics, theme }) => theme.scale.px($metrics.identityColumnGapPx)};
`;

const NameRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.scale.px(8)};
`;

const Name = styled.p<{ $metrics: ProfileSummaryCardMetrics }>`
  ${rubikTypographyStyles};
  margin: 0;
  white-space: pre-line;
  overflow-wrap: anywhere;
  font-size: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.nameTextSizePx)};
  font-weight: 600;
  line-height: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.nameLineHeightPx)};
`;

const GoalText = styled.p<{ $metrics: ProfileSummaryCardMetrics }>`
  margin: 0;
  white-space: pre-line;
  overflow-wrap: anywhere;
  word-break: break-word;
  user-select: text;
  -webkit-user-select: text;
  font-size: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.goalTextSizePx)};
  line-height: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.goalLineHeightPx)};
`;

const ProgressTrack = styled.div<{
  $metrics: ProfileSummaryCardMetrics;
  $tone: ProfileSummaryCardProgressTrackTone;
}>`
  position: relative;
  overflow: hidden;
  height: ${({ $metrics, theme }) => theme.scale.px($metrics.progressHeightPx)};
  border-radius: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.progressRadiusPx)};
  background: ${({ $tone, theme }) =>
    resolveProgressTrackBackground($tone, theme)};
`;

const ProgressFill = styled.div<{
  $metrics: ProfileSummaryCardMetrics;
  $progressPercent: number;
}>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $progressPercent }) => `${$progressPercent}%`};
  height: 100%;
  border-radius: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.progressRadiusPx)};
  background: ${({ theme }) => theme.colors.brand.primary};
`;

const ProgressLabel = styled.p<{ $metrics: ProfileSummaryCardMetrics }>`
  ${rubikTypographyStyles};
  margin: 0;
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-size: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.progressLabelSizePx)};
  line-height: ${({ $metrics, theme }) =>
    theme.scale.px($metrics.progressLabelLineHeightPx)};
`;

export function ProfileSummaryCard({
  avatar,
  avatarSlotWidthPx,
  dataUi,
  goalText,
  profileName,
  progressLabel,
  progressPercent,
  progressTrackTone = 'surface',
  ratingSlot,
  variant = 'default',
}: ProfileSummaryCardProps) {
  const metrics = profileSummaryCardMetricsByVariant[variant];
  const resolvedAvatarSlotWidthPx =
    avatarSlotWidthPx ??
    (variant === 'compact' ? 49 : variant === 'preview' ? 64 : 74);

  return (
    <Card $dataUi={dataUi} $metrics={metrics}>
      <IdentityRow
        $avatarSlotWidthPx={resolvedAvatarSlotWidthPx}
        $metrics={metrics}
      >
        <AvatarSlot>{avatar}</AvatarSlot>

        <IdentityColumn $metrics={metrics}>
          <NameRow>
            <Name $metrics={metrics}>{profileName}</Name>
            {ratingSlot}
          </NameRow>

          <GoalText $metrics={metrics}>{goalText}</GoalText>
        </IdentityColumn>
      </IdentityRow>

      {progressLabel !== null && progressPercent !== null ? (
        <ProgressTrack $metrics={metrics} $tone={progressTrackTone}>
          <ProgressFill $metrics={metrics} $progressPercent={progressPercent} />
          <ProgressLabel $metrics={metrics}>{progressLabel}</ProgressLabel>
        </ProgressTrack>
      ) : null}
    </Card>
  );
}
